import { Button, Form, Input, Modal, Tooltip, message } from "antd";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCalendarDays,
  faCircleExclamation,
  faEye,
  faLocationDot,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  trackBookingCancellationAbandoned,
  trackBookingCancellationInitiated,
  trackBookingCancellationReasonSelected,
  trackBookingCancellationSubmitted,
} from "analytics";
import {
  CANCELLATION_REASON_OPTIONS,
  CancellationReasonCategory,
  canCancelBookingFromApplicationDate,
  getCancellationTiming,
} from "helpers/booking-cancellation-helper";
import {
  CancelBookingRequestData,
  CancelBookingCreator,
} from "helpers/cancel-booking-request-helper";
import { BusinessState } from "redux-context/business";
import { UserState } from "redux-context/user";
import { cancelBusinessBooking } from "services/SauceService";
import "./CancelBookingRequest.scss";

interface CancelBookingRequestProps {
  open?: boolean;
  asButton?: boolean;
  triggerVariant?: "button" | "text" | "menu_item";
  onTriggerClick?: () => void;
  disabled?: boolean;
  bookingId: CancelBookingRequestData["bookingId"];
  collabId: CancelBookingRequestData["collabId"];
  entryPoint: CancelBookingRequestData["entryPoint"];
  applicationDate?: CancelBookingRequestData["applicationDate"];
  venueName?: CancelBookingRequestData["venueName"];
  venueAddress?: CancelBookingRequestData["venueAddress"];
  creator?: CancelBookingCreator | null;
  callback?: () => void;
}

type CancelBookingFormValues = {
  reasonCategory?: CancellationReasonCategory;
  reasonText?: string;
};

interface CancellationReasonCardsProps {
  value?: CancellationReasonCategory;
  onChange?: (value: CancellationReasonCategory) => void;
  onReasonSelect: (value: CancellationReasonCategory) => void;
}

const LATE_CANCELLATION_WARNING =
  "Important Notice: If this booking is canceled close to the scheduled time, and the creator has already traveled or arrives at the venue, compensation for their time may apply. We will review the situation and notify you if reimbursement is required.";

const toLocalIsoTimestamp = (date: Date): string => {
  const pad = (value: number, size = 2): string =>
    String(Math.trunc(Math.abs(value))).padStart(size, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = pad(date.getMilliseconds(), 3);

  const offsetTotalMinutes = -date.getTimezoneOffset();
  const offsetSign = offsetTotalMinutes >= 0 ? "+" : "-";
  const offsetHours = pad(Math.floor(Math.abs(offsetTotalMinutes) / 60));
  const offsetMinutes = pad(Math.abs(offsetTotalMinutes) % 60);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
};

const CancellationReasonCards: React.FC<CancellationReasonCardsProps> = ({
  value,
  onChange,
  onReasonSelect,
}) => {
  return (
    <div
      className="cancel-booking-modal__reasons"
      data-testid="cancel-reason-select"
    >
      {CANCELLATION_REASON_OPTIONS.map((option) => {
        const isSelected = value === option.value;

        return (
          <button
            key={option.value}
            type="button"
            className={`cancel-booking-modal__reason-card ${
              isSelected ? "selected" : ""
            }`.trim()}
            onClick={() => {
              onChange?.(option.value);
              onReasonSelect(option.value);
            }}
            aria-pressed={isSelected}
          >
            <span
              className={`cancel-booking-modal__reason-radio ${
                isSelected ? "selected" : ""
              }`.trim()}
            >
              <span className="cancel-booking-modal__reason-dot" />
            </span>
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const CancelBookingRequest: React.FC<CancelBookingRequestProps> = ({
  bookingId,
  collabId,
  entryPoint,
  applicationDate,
  venueName,
  venueAddress,
  creator,
  asButton,
  triggerVariant,
  onTriggerClick,
  disabled = false,
  open = false,
  callback,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loggedUser } = useSelector((state: UserState) => state.user);
  const { business, subscription } = useSelector(
    (state: BusinessState) => state.business,
  );
  const [form] = Form.useForm<CancelBookingFormValues>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(open);
  const hasTrackedInitiatedRef = useRef(false);
  const hasInteractedRef = useRef(false);
  const reasonCategory = Form.useWatch("reasonCategory", form);
  const reasonText = Form.useWatch("reasonText", form);

  const canCancelNow = canCancelBookingFromApplicationDate(applicationDate);
  const isCancelActionDisabled = disabled || !canCancelNow;

  useEffect(() => {
    setShowCancelModal(open && !isCancelActionDisabled);
  }, [isCancelActionDisabled, open]);

  const timingPreview = getCancellationTiming(applicationDate);
  const isLastMinutePreview = Boolean(timingPreview?.isLastMinute);

  const businessId =
    business?._id ?? localStorage.getItem("businessId") ?? null;

  const creatorDisplayName = creator?.name || creator?.username || "Creator";
  const creatorInitial =
    creatorDisplayName.trim().charAt(0).toUpperCase() || "C";
  const venueDisplay =
    [venueName, venueAddress].filter(Boolean).join(", ") ||
    "Location to be confirmed";

  const bookingDateTimeDisplay = useMemo(() => {
    const dateRaw = applicationDate?.date;
    const hourRaw = applicationDate?.hour;

    let dateDisplay = "Date to be confirmed";
    if (dateRaw) {
      const parsed = dayjs(dateRaw);
      if (parsed.isValid()) {
        dateDisplay = parsed.format("MMMM D, YYYY");
      }
    }

    if (!hourRaw) return dateDisplay;
    return `${dateDisplay} - ${hourRaw}`;
  }, [applicationDate?.date, applicationDate?.hour]);

  const analyticsBaseContext = useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search,
      businessId,
      userId: loggedUser?.id ?? null,
      userEmail: loggedUser?.email ?? null,
      planType: subscription?.planName ?? null,
      currency:
        subscription?.stripeDetails?.nextPaymentDetails?.currency ?? null,
      bookingId,
      campaignId: collabId,
      restaurantId: businessId,
      creatorId: creator?.id ?? null,
      entryPoint,
    }),
    [
      businessId,
      bookingId,
      collabId,
      creator?.id,
      entryPoint,
      location.pathname,
      location.search,
      loggedUser?.email,
      loggedUser?.id,
      subscription?.planName,
      subscription?.stripeDetails?.nextPaymentDetails?.currency,
    ],
  );

  const buildAnalyticsContext = useCallback(
    (params?: { reason?: CancellationReasonCategory | null; now?: Date }) => {
      const now = params?.now ?? new Date();
      const timing = getCancellationTiming(applicationDate, 240, now);
      return {
        ...analyticsBaseContext,
        reason: params?.reason ?? null,
        timeBeforeEventMinutes: timing?.timeBeforeEventMinutes ?? null,
        isLastMinute: timing?.isLastMinute ?? null,
      };
    },
    [analyticsBaseContext, applicationDate],
  );

  const closeModal = (trackAbandoned = true) => {
    if (trackAbandoned && hasInteractedRef.current) {
      trackBookingCancellationAbandoned(
        buildAnalyticsContext({
          reason:
            (reasonCategory as CancellationReasonCategory | undefined) ?? null,
        }),
      );
    }

    form.resetFields();
    setShowCancelModal(false);
    setLoading(false);
    hasTrackedInitiatedRef.current = false;
    hasInteractedRef.current = false;
  };

  const openModal = () => {
    if (isCancelActionDisabled) return;
    onTriggerClick?.();
    setShowCancelModal(true);
  };

  const onReasonChange = (value: CancellationReasonCategory) => {
    hasInteractedRef.current = true;
    trackBookingCancellationReasonSelected(
      buildAnalyticsContext({ reason: value }),
    );
  };

  useEffect(() => {
    if (!showCancelModal || hasTrackedInitiatedRef.current) {
      return;
    }

    hasTrackedInitiatedRef.current = true;
    hasInteractedRef.current = false;
    trackBookingCancellationInitiated(buildAnalyticsContext());
  }, [buildAnalyticsContext, showCancelModal]);

  useEffect(() => {
    if (!showCancelModal) return;
    if (reasonCategory || String(reasonText || "").trim()) {
      hasInteractedRef.current = true;
    }
  }, [reasonCategory, reasonText, showCancelModal]);

  useEffect(() => {
    if (!showCancelModal) return;
    return () => {
      hasTrackedInitiatedRef.current = false;
      hasInteractedRef.current = false;
    };
  }, [showCancelModal]);

  useEffect(() => {
    if (!open) {
      hasTrackedInitiatedRef.current = false;
      hasInteractedRef.current = false;
    }
  }, [open]);

  const markInteractionFromText = () => {
    hasInteractedRef.current = true;
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    const now = new Date();
    const timing = getCancellationTiming(applicationDate, 240, now);

    if (!timing || timing.timeBeforeEventMinutes < 0) {
      message.error("This booking can no longer be canceled.");
      closeModal();
      return;
    }

    setLoading(true);
    try {
      await cancelBusinessBooking({
        bookingId,
        collabId,
        reasonCategory: values.reasonCategory as CancellationReasonCategory,
        reasonText: String(values.reasonText || "").trim(),
        cancellationTimestamp: now.toISOString(),
        cancellationLocalTimestamp: toLocalIsoTimestamp(now),
        timeBeforeEventMinutes: timing.timeBeforeEventMinutes,
        isLastMinute: timing.isLastMinute,
      });

      trackBookingCancellationSubmitted(
        buildAnalyticsContext({
          reason: values.reasonCategory as CancellationReasonCategory,
          now,
        }),
      );

      message.success("Booking canceled successfully.");
      closeModal(false);

      if (callback) {
        callback();
      } else {
        navigate(".", { replace: true });
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Error canceling booking.";
      message.error(errorMessage);
      setLoading(false);
    }
  };

  const resolvedVariant = triggerVariant ?? (asButton ? "button" : "text");

  const triggerNode =
    resolvedVariant === "menu_item" ? (
      <button
        type="button"
        disabled={isCancelActionDisabled}
        className={`manage-booking-actions__menu-item ${isCancelActionDisabled ? "manage-booking-actions__menu-item--disabled" : ""}`.trim()}
        onClick={openModal}
      >
        <FontAwesomeIcon icon={faXmark} />
        <span>Cancel booking</span>
      </button>
    ) : resolvedVariant === "button" ? (
      <Tooltip title="Cancel this booking">
        <Button
          className="button-icon red-hover"
          icon={<FontAwesomeIcon icon={faBan} style={{ fontSize: 12 }} />}
          style={{ fontSize: 12 }}
          disabled={isCancelActionDisabled}
          onClick={openModal}
        >
          Cancel
        </Button>
      </Tooltip>
    ) : (
      <span
        onClick={openModal}
        style={{
          cursor: isCancelActionDisabled ? "not-allowed" : "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          color: "#ef4444",
          fontSize: 14,
          fontWeight: 500,
          opacity: isCancelActionDisabled ? 0.7 : 1,
        }}
      >
        <FontAwesomeIcon icon={faBan} />
        Cancel
      </span>
    );

  const renderWarningBlock = () => (
    <div className="cancel-booking-modal__warning">
      <FontAwesomeIcon icon={faCircleExclamation} />
      <div>
        <strong>Important Notice</strong>
        <p>{LATE_CANCELLATION_WARNING}</p>
      </div>
    </div>
  );

  return (
    <>
      {triggerNode}

      <Modal
        open={showCancelModal}
        title={null}
        closable={false}
        onCancel={() => closeModal()}
        footer={null}
        width={760}
        className="cancel-booking-modal"
        rootClassName="cancel-booking-modal-root"
        styles={{
          wrapper: {
            zIndex: 999999,
          },
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          className="cancel-booking-modal__layout"
        >
          <div className="cancel-booking-modal__header">
            <button
              type="button"
              className="cancel-booking-modal__close"
              onClick={() => closeModal()}
              aria-label="Close cancellation modal"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>

            {isLastMinutePreview && (
              <div className="cancel-booking-modal__warning--mobile">
                {renderWarningBlock()}
              </div>
            )}

            <h2>Cancel this confirmed booking?</h2>
            <p>
              This will immediately notify the creator regarding the
              cancellation.
            </p>
          </div>

          <div className="cancel-booking-modal__body">
            <div className="cancel-booking-modal__summary">
              <div className="cancel-booking-modal__avatar-wrap">
                {creator?.avatarUrl ? (
                  <img
                    src={creator.avatarUrl}
                    alt={creatorDisplayName}
                    className="cancel-booking-modal__avatar"
                  />
                ) : (
                  <div className="cancel-booking-modal__avatar cancel-booking-modal__avatar--fallback">
                    {creatorInitial}
                  </div>
                )}
                <span className="cancel-booking-modal__avatar-status" />
              </div>

              <div className="cancel-booking-modal__summary-content">
                <h3>{creatorDisplayName}</h3>
                <div className="cancel-booking-modal__summary-row">
                  <FontAwesomeIcon icon={faCalendarDays} />
                  <span>{bookingDateTimeDisplay}</span>
                </div>
                <div className="cancel-booking-modal__summary-row">
                  <FontAwesomeIcon icon={faLocationDot} />
                  <span>{venueDisplay}</span>
                </div>
              </div>
            </div>

            <Form.Item
              label="Why are you canceling?"
              name="reasonCategory"
              rules={[{ required: true, message: "Please select a reason." }]}
              className="cancel-booking-modal__form-item"
            >
              <CancellationReasonCards onReasonSelect={onReasonChange} />
            </Form.Item>

            <Form.Item
              label={
                <span>
                  Please provide details{" "}
                  <span className="cancel-booking-modal__required-label">
                    (required)
                  </span>
                </span>
              }
              name="reasonText"
              rules={[
                { required: true, message: "Please add an explanation." },
                {
                  validator: (_, value) => {
                    if (
                      typeof value !== "string" ||
                      value.trim().length === 0
                    ) {
                      return Promise.reject(
                        new Error("Please add an explanation."),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              className="cancel-booking-modal__form-item cancel-booking-modal__details-section"
            >
              <Input.TextArea
                rows={4}
                placeholder="Please explain the reason for this cancellation..."
                onChange={markInteractionFromText}
                className="cancel-booking-modal__textarea"
              />
            </Form.Item>

            <p className="cancel-booking-modal__hint">
              <FontAwesomeIcon icon={faEye} />
              This message will be shared with the creator.
            </p>

            {isLastMinutePreview && (
              <div className="cancel-booking-modal__warning--desktop">
                {renderWarningBlock()}
              </div>
            )}
          </div>

          <div className="cancel-booking-modal__footer">
            <Button
              onClick={() => closeModal()}
              disabled={loading}
              className="cancel-booking-modal__keep-btn"
            >
              Keep Booking
            </Button>
            <Button
              type="primary"
              danger
              htmlType="submit"
              loading={loading}
              disabled={!reasonCategory || !String(reasonText || "").trim()}
              className="cancel-booking-modal__cancel-btn"
            >
              Cancel Booking
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default CancelBookingRequest;
